/**
 * Coaching Metrics Configuration (Frontend)
 * 
 * This file defines all coaching metrics for UI display in the Driver Feedback settings page.
 * 
 * HOW TO ADD A NEW COACHING METRIC:
 * ===================================
 * 1. Add entry to weeklyPerformanceMetrics or dailyPerformanceMetrics array
 * 2. Required properties:
 *    - name: Must match backend config (e.g., 'NewMetric')
 *    - src: Must match backend config (e.g., 'Scorecard')
 *    - sliderField: Must match backend config (e.g., 'ScorecardNewMetric')
 *    - label: Display text shown in UI
 * 
 * 3. Optional UI properties:
 *    - header: Group header text (only on first metric of group)
 *    - negativeToggleOnly: true = only Issue/CO toggles (no PR/Kudo)
 *    - positiveToggleOnly: true = only PR/Kudo toggles (no Issue/CO)
 *    - polarity: 1 (higher is better) or -1 (lower is better)
 *    - type: 'number', 'percent' (for display formatting)
 *    - tooltip: Help text shown on hover
 *    - min: Minimum input value
 *    - thresholdIssueMax, thresholdMax, thresholdPRMax, thresholdKudoMax: Max input values
 * 
 * 4. Update backend file: amplify/backend/function/tenant/src/config/coachingMetricsConfig.js
 *    (Backend only needs: name, src, sliderField)
 * 
 * EXAMPLE:
 * {
 *   name: 'NewMetric',
 *   src: 'Scorecard',
 *   sliderField: 'ScorecardNewMetric',
 *   label: 'New Metric',
 *   negativeToggleOnly: false,
 *   polarity: 1,
 *   type: 'number',
 *   tooltip: 'Description of the metric'
 * }
 */

export var weeklyPerformanceMetrics = [
    {
        sliderField: 'CDFDPMO', src: 'Scorecard',  header: 'Scorecard', negativeToggleOnly: false, polarity: -1, label: 'CDF DPMO', name: 'CdfDpmo', type: 'number', tooltip: '',
        thresholdIssueMax: Infinity, thresholdMax: Infinity, thresholdPRMax: Infinity, thresholdKudoMax: Infinity,
    },
    {
        sliderField: 'ScorecardConsecutiveTier', src: 'Scorecard', name: 'ConsecutiveTier', label: 'Consecutive Overall Tier', tooltip: "Please reference Amazon's Metric Guide for further information on how the score is calculated."
    },
    {
        sliderField: 'ScorecardDAR', src: 'Scorecard', negativeToggleOnly: false, polarity: 1, label: 'DAR', name: 'Dar', type: 'number', tooltip: 'Delivered And Received rate'
    },
    {
        sliderField: 'ScorecardDCR', src: 'Scorecard', negativeToggleOnly: false, polarity: 1, label: 'DCR', name: 'Dcr', type: 'percent', tooltip: 'Delivery Completion Rate (Note: the "Fantastic" threshold may vary by station - please check with your station'
    },
    {
        sliderField: 'ScorecardDCDPMO', src: 'Scorecard', negativeToggleOnly: false, polarity: -1, label: 'DC DPMO', name: 'DcDpmo', type: 'number', tooltip: 'Delivery Completion Defects Per Million Opportunities',
        thresholdIssueMax: Infinity, thresholdMax: Infinity, thresholdPRMax: Infinity, thresholdKudoMax: Infinity,
    },
    {
        sliderField: 'ScorecardDistractionsRate', src: 'Scorecard', negativeToggleOnly: false, polarity: -1, label: 'Distractions Rate', name: 'DistractionsRate', type: 'number', tooltip: "Please reference Amazon's Metric Guide for further information on how the score is calculated."
    },
    {
        sliderField: 'ScorecardDNR', src: 'Scorecard', negativeToggleOnly: false, polarity: -1, label: 'DNR', name: 'Dnr', type: 'number', tooltip: 'Did Not Receive rate'
    },
    {
        sliderField: 'ScorecardDSB', src: 'Scorecard', negativeToggleOnly: false, polarity: -1, label: 'DSB', name: 'Dsb', type: 'number', tooltip: 'Delivery Success Behaviors'
    },
    {
        sliderField: 'ScorecardFICOScore', src: 'Scorecard', negativeToggleOnly: false, polarity: 1, label: 'FICO® Score', name: 'Fico', type: 'number', tooltip: ''
    },
    {
        sliderField: 'ScorecardPSB', src: 'Scorecard', negativeToggleOnly: false, polarity: -1, label: 'PSB', name: 'Psb', type: 'number', tooltip: 'Pickup Success Behaviors'
    },
    {
        sliderField: 'ScorecardFollowingDistanceRate', src: 'Scorecard', negativeToggleOnly: false, polarity: -1, label: 'Following Distance Rate', name: 'FollowingDistanceRate', type: 'number', tooltip: "Please reference Amazon's Metric Guide for further information on how the score is calculated."
    },
    {
        sliderField: 'ScorecardHarshBrakingRate', src: 'Scorecard', negativeToggleOnly: false, polarity: -1, label: 'Harsh Braking Rate', name: 'HarshBrakingRate', type: 'number', tooltip: ''
    },
    {
        sliderField: 'ScorecardHarshCorneringRate', src: 'Scorecard', negativeToggleOnly: false, polarity: -1, label: 'Harsh Cornering Rate', name: 'HarshCorneringRate', type: 'number', tooltip: ''
    },
    {
        sliderField: 'ScorecardOverallTier', src: 'Scorecard', name: 'OverallTier', label: 'Overall Tier', tooltip: "Please reference Amazon's Metric Guide for further information on how the score is calculated."
    },
    {
        sliderField: 'ScorecardSeatbeltOffRate', src: 'Scorecard', negativeToggleOnly: false, polarity: -1, label: 'Seatbelt-Off Rate', name: 'SeatbeltOff', type: 'number', tooltip: ''
    },
    {
        sliderField: 'ScorecardSignSignalViolationRate', src: 'Scorecard', negativeToggleOnly: false, polarity: -1, label: 'Sign/Signal Violation Rate', name: 'SignSignalViolationsRate', type: 'number', tooltip: "Please reference Amazon's Metric Guide for further information on how the score is calculated."
    },
    {
        sliderField: 'ScorecardSpeedingEventRate', src: 'Scorecard', negativeToggleOnly: false, polarity: -1, label: 'Speeding Event Rate', name: 'SpeedingEvent', type: 'number', tooltip: ''
    },
    {
        sliderField: 'ScorecardSWCPOD', src: 'Scorecard', negativeToggleOnly: false, polarity: 1, label: 'SWC-POD', name: 'SwcPod', type: 'percent', tooltip: 'Photo on Delivery score'
    },
   
    {
        sliderField: 'ScorecardSWCSC', src: 'Scorecard', negativeToggleOnly: false, polarity: 1, label: 'SWC-SC', name: 'SwcSc', type: 'percent', tooltip: 'Scan Compliance percentage'
    },
    
    {
        sliderField: 'CXCdfScore', src: 'CXFeedback', header: 'CX Feedback (for weeks prior to W34, 2024)', negativeToggleOnly: false, polarity: 1, label: 'CDF Score', name: 'CdfScore', type: 'number', tooltip: ""
    },
    {
        sliderField: 'CXDaTier', src: 'CXFeedback',  name: 'DaTier', label: 'DA Tier'
    },

    {
        sliderField: 'PPSTotalBreaches', src: 'ProperParkingSequence(PPS)*',  header: 'Proper Parking Sequence (PPS)*',  negativeToggleOnly: false, polarity: -1, label: 'PPS Breaches', name: 'PpsBreaches', type: 'number', tooltip: '',
        thresholdIssueMax: Infinity, thresholdMax: Infinity, thresholdPRMax: Infinity, thresholdKudoMax: Infinity,
    },
    {
        sliderField: 'PPSCompliance', src: 'ProperParkingSequence(PPS)*', negativeToggleOnly: false, polarity: 1, label: 'PPS Compliance %', name: 'PpsCompliancePercent', type: 'percent', tooltip: '',
        thresholdIssueMax: 100, thresholdMax: 100, thresholdPRMax: 100, thresholdKudoMax: 100,
    },
];

export var scorecardAndCxMetricsMaxInputValue = {
    thresholdIssueMax: 850,
    thresholdMax: 850,
    thresholdPRMax: 850,
    thresholdKudoMax: 850,
}

export var dailyPerformanceMetrics = [
    {
        sliderField: 'MentorDailyFICO', src: 'Mentor', header: 'Mentor', negativeToggleOnly: false, polarity: 1, label: 'Daily FICO® Score', name: 'DailyFico', type: 'number', tooltip: ''
    },
    {
        sliderField: 'MentorSeatbeltRate', src: 'Mentor', negativeToggleOnly: false, polarity: -1, label: 'Seatbelt', name: 'Seatbelt', type: 'number', tooltip: ''
    },
    {
        sliderField: 'MentorSse', src: 'Mentor', negativeToggleOnly: false, polarity: -1, label: 'Speeding/Sse', name: 'Sse', type: 'number', tooltip: 'If either Speeding or Sse crosses a threshold, the corresponding item will be created.'
    },
    {
        sliderField: 'MentorTrainingsRemaining', src: 'Mentor', negativeToggleOnly: true, polarity: 1, label: 'Trainings Remaining', name: 'TraningRemaining', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneDriverStar', src: 'Netradyne', header: 'Netradyne', positiveToggleOnly: true, min: 0, polarity: 1, label: 'Driver Star', name: 'DriverStar', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneCameraObstruction', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Camera Obstruction', name: 'CameraObstruction', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneDriverDistraction', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Driver Distraction', name: 'DriverDistraction', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneDriverDrowsiness', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Driver Drowsiness', name: 'DriverDrowsiness', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneFollowingDistance', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Following Distance', name: 'FollowingDistance', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneHardAcceleration', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Hard Acceleration', name: 'HardAcceleration', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneHardBraking', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Hard Braking', name: 'HardBraking', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneHardTurn', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Hard Turn', name: 'HardTurn', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneHighG', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'High G', name: 'HighG', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneLowImpact', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Low Impact', name: 'LowImpact', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneSeatbeltCompliance', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Seatbelt Compliance', name: 'SeatbeltCompliance', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneSignViolations', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Sign Violations', name: 'SignViolations', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneSpeedingViolations', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Speeding Violations', name: 'SpeedingViolations', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneTrafficLightViolation', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Traffic Light Violation', name: 'TrafficLightViolation', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneUturn', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'U-turn', name: 'UTurn', type: 'number', tooltip: ''
    },
    {
        sliderField: 'NetradyneWeaving', src: 'Netradyne', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Weaving', name: 'Weaving', type: 'number', tooltip: ''
    },
    {
        sliderField: 'EOCDailyComplianceRate', src: 'EOC', header: 'Engine Off Compliance (EOC)', negativeToggleOnly: false, min: 0, polarity: 1, label: 'Average Daily Compliance', name: 'DailyComplianceRate', type: 'percent', tooltip: 'Daily Compliance percentage'
    },
    {
        sliderField: 'PPSCompliance(%)', src: 'ProperParkingSequence(PPS)', header: 'Proper Parking Sequence (PPS)', negativeToggleOnly: false, min: 0, polarity: 1, label: 'PPS Compliance (%)', name: 'DailyPpsCompliancePercent', type: 'percent', tooltip: ''
    },
    {
        sliderField: 'MissingParkingBrake(%)', src: 'ProperParkingSequence(PPS)', negativeToggleOnly: false, min: 0, polarity: -1, label: 'Missing Parking Brake (%)', name: 'DailyPpsMissingBrake', type: 'percent', tooltip: ''
    },
    {
        sliderField: 'MissingGearinPark(%)', src: 'ProperParkingSequence(PPS)', negativeToggleOnly: false, min: 0, polarity: -1, label: 'Missing Gear in Park (%)', name: 'DailyPpsMissingGear', type: 'percent', tooltip: ''
    }, 
    {
        sliderField: 'CDFDaMishandledPackage', src: 'CDF',  header: 'Negative Customer Delivery Feedback (CDF)', negativeToggleOnly: true, min: 0, polarity: -1, label: 'DA Mishandled Package', name: 'DaMishandledPackage', type: 'number', tooltip: ''
    },
    {
        sliderField: 'CDFDaWasUnprofessional', src: 'CDF', negativeToggleOnly: true, min: 0, polarity: -1, label: 'DA was Unprofessional', name: 'DaWasUnprofessional', type: 'number', tooltip: ''
    },
    {
        sliderField: 'CDFDaIgnoreInstructions', src: 'CDF', negativeToggleOnly: true, min: 0, polarity: -1, label: 'DA did not follow my delivery instructions', name: 'DaIgnoreInstructions', type: 'number', tooltip: ''
    },
    {
        sliderField: 'CDFDeliveredToWrongAddress', src: 'CDF', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Delivered to Wrong Address', name: 'DeliveredToWrongAddress', type: 'number', tooltip: ''
    },
    {
        sliderField: 'CDFNeverReceivedDelivery', src: 'CDF', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Never Received Delivery', name: 'NeverReceivedDelivery', type: 'number', tooltip: ''
    },
    {
        sliderField: 'CDFReceivedWrongItem', src: 'CDF', negativeToggleOnly: true, min: 0, polarity: -1, label: 'Received Wrong Item', name: 'ReceivedWrongItem', type: 'number', tooltip: ''
    },
];